import { html } from 'lit-element';

import { LightLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import { actions } from '@elab/core/store/wishlist';
import { actions as cartActions } from '@elab/core/store/cart';
import { actions as globalActions } from '@elab/core/store/global';
import { actions as uiActions } from '@elab/core/store/ui';

/**
 * Component display "Add to Cart" button,
 * if the item is not in stock, shows the disabled button with the label not in stock
 *
 * @author Zhynzher Oleksandr
 *
 * @attr { String }  [redirect-url] - url for onclick redirect
 * @attr { String }  [product-code] - unique code of the product
 * @attr { String }  [product-wants] - how many products user wants to buy
 * @attr { String }  [is-out-of-stock] - information about stock status about the product
 * @attr { String }  [add-to-cart-label] - Is label for "add to cart" button
 * @attr { String }  [out-of-stock-label] - Is label for "out of stock" notification
 * @attr { Boolean } [is-shared-product] - Product has come from a shared wishlist
 * @attr { Boolean } [show-out-of-stock-button] - should we render out of stock button or slot
 * @attr { Boolean } [show-confirmation-dialog] - define should be shown confirmation dialog after adding product to cart
 *
 * HTML Structure Example
 *
 * <e2-wishlist-add-to-cart
 *      product-code="Product code"
 *      product-wants="Product wants"
 *      add-to-cart-label="Add to cart label"
 *      out-of-stock-label="Out of stock label"
 *      is-out-of-stock="Product stock level status"
 *      is-shared-product
 *      show-confirmation-dialog
 *      show-out-of-stock-button>
 * </e2-wishlist-add-to-cart>
 *
 * */

export class WishlistAddToCart extends LightLitElement {
    static get properties() {
        return {
            redirectUrl: { type: String, attribute: 'redirect-url' },
            productCode: { type: String, attribute: 'product-code' },
            productWants: { type: String, attribute: 'product-wants' },
            isOutOfStock: {
                type: Boolean,
                attribute: 'is-out-of-stock',
            },
            addToCartLabel: { type: String, attribute: 'add-to-cart-label' },
            outOfStockLabel: { type: String, attribute: 'out-of-stock-label' },
            isSharedProduct: { type: Boolean, attribute: 'is-shared-product' },
            showOutOfStockButton: {
                type: Boolean,
                attribute: 'show-out-of-stock-button',
            },
            showConfirmationDialog: {
                type: Boolean,
                attribute: 'show-confirmation-dialog',
            },
            unavailableCountryLabel: { type: String, attribute: 'unavailable-country-label' },
            isForbidden: { type: Boolean, attribute: 'is-forbidden' },
        };
    }

    handleAddToCart = () => {
        if (this.redirectUrl) {
            this.dispatchEvent(new CustomEvent('redirect', { detail: this.redirectUrl }));

            return;
        }

        const detail = {
            productCode: this.productCode,
            quantity: this.productWants,
            showConfirmationDialog: this.showConfirmationDialog,
            analyticsData: this.dataset,
        };

        if (this.isSharedProduct) {
            this.dispatchEvent(new CustomEvent('add-shared-product', { detail }));
        } else {
            this.dispatchEvent(new CustomEvent('add-to-cart', { detail }));
        }
    };

    renderAddToCartButton() {
        return html`
            <button class="wishlist-add-to-cart__button" @click="${this.handleAddToCart}">
                <span class="wishlist-add-to-cart__label">${this.addToCartLabel}</span>
                <i class="wishlist-add-to-cart__icon"></i>
            </button>
        `;
    }

    renderOutOfStockInfo() {
        return html`
            <e2-button
                size=""
                class-modifier="wishlist__add-to-cart-button
                        wishlist__add-to-cart-button--disabled
                        ${this.showOutOfStockButton ? 'd-contents' : 'd-none'}"
                no-arrow
                full-width
                ?disabled="${this.isForbidden}"
                button-label="${this.isForbidden
                    ? this.unavailableCountryLabel
                    : this.outOfStockLabel}"
            ></e2-button>
            <v-slot
                class="${this.showOutOfStockButton ? 'd-none' : 'd-contents'}"
                name="out-of-stock"
            ></v-slot>
        `;
    }

    render() {
        return html`
            <div class="wishlist-add-to-cart">
                ${this.isOutOfStock || this.isForbidden
                    ? this.renderOutOfStockInfo()
                    : this.renderAddToCartButton()}
            </div>
        `;
    }
}

const mapDispatchToProps = (dispatch, ctx) => ({
    'add-to-cart': ({ detail }) => {
        if (ctx.showConfirmationDialog) {
            dispatch(
                uiActions.setMinicartPopoverData({
                    productCode: detail.productCode,
                    quantityAdded: detail.quantity,
                }),
            );
        }
        dispatch(actions.addToCart(detail));
    },
    'add-shared-product': ({ detail }) => {
        dispatch(
            cartActions.addToCart({
                code: detail.productCode,
                quantity: detail.quantity,
            }),
        );
    },
    redirect: ({ detail }) => dispatch(globalActions.redirect(detail)),
});

export default () =>
    customElements.define(
        'e2-wishlist-add-to-cart',
        connect(null, mapDispatchToProps)(WishlistAddToCart),
    );
